// =============================================================================
// gtfs-status.js – Feed GTFS validity state for in-app warnings
// =============================================================================
// Provides:
//   - getFeedStatus(cfg, statusJson?) → { state, from, to, daysLeft }
//   - fetchGtfsStatus() → fetch /gtfs-status.json, fallback to cfg.feedValidity
// =============================================================================

import { CFG } from "../data/config.js";

const GTFS_STATUS_URL = "./gtfs-status.json";
const EXPIRING_THRESHOLD_DAYS = 7; // configurable threshold

/**
 * Compute the feed status from config and optional remote status JSON.
 *
 * @param {object} cfg – app config (must have cfg.feedValidity.from / .to)
 * @param {object|null} statusJson – remote gtfs-status.json (may override feedValidity)
 * @returns {{ state: "ok"|"expiring"|"expired", from: string, to: string, daysLeft: number }}
 */
export function getFeedStatus(cfg, statusJson = null) {
  // Prefer remote status if it has feedValidity
  const validity = statusJson?.feedValidity || cfg?.feedValidity;

  if (!validity || !validity.to) {
    return { state: "ok", from: null, to: null, daysLeft: Infinity };
  }

  const from = validity.from || null;
  const to = validity.to;

  const now = new Date();
  // Parse "to" as end-of-day in local time
  const toDate = new Date(to + "T23:59:59");
  const diffMs = toDate.getTime() - now.getTime();
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  let state = "ok";
  if (daysLeft <= 0) {
    state = "expired";
  } else if (daysLeft <= EXPIRING_THRESHOLD_DAYS) {
    state = "expiring";
  }

  return { state, from, to, daysLeft };
}

/**
 * Fetch the GTFS status from /gtfs-status.json and return the computed state.
 * Falls back to cfg.feedValidity if the fetch fails or returns empty.
 *
 * @returns {Promise<{ state: "ok"|"expiring"|"expired", from: string|null, to: string|null, daysLeft: number }>}
 */
export async function fetchGtfsStatus() {
  let statusJson = null;
  try {
    const res = await fetch(GTFS_STATUS_URL, { cache: "no-cache" });
    if (res.ok) {
      const data = await res.json();
      if (data && data.feedValidity) {
        statusJson = data;
      }
    }
  } catch {
    // Silently fall back to config
  }

  return getFeedStatus(CFG, statusJson);
}
